import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  SquarePurchases,
  SquarePurchasesItem,
  SquarePurchasesProgress,
} from "@shared/square-purchases";

type SortKey = "name" | "variationName" | "quantity" | "grossCents" | "discountCents" | "taxCents" | "collectedCents" | "percentOfTotal";
const ITEMS_PER_PAGE = 15;

function compareExactDecimal(left: string, right: string) {
  const parse = (value: string) => {
    const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(value);
    if (!match) return null;
    const fraction = match[3] ?? "";
    const scale = 10n ** BigInt(fraction.length);
    const magnitude = BigInt(match[2]) * scale + BigInt(fraction || "0");
    return match[1] === "-" ? -magnitude : magnitude;
  };
  const a = parse(left);
  const b = parse(right);
  if (a == null || b == null) return left.localeCompare(right, undefined, { numeric: true });
  const leftPlaces = left.split(".")[1]?.length ?? 0;
  const rightPlaces = right.split(".")[1]?.length ?? 0;
  const scale = 10n ** BigInt(Math.max(leftPlaces, rightPlaces));
  const scaledA = a * (scale / 10n ** BigInt(leftPlaces));
  const scaledB = b * (scale / 10n ** BigInt(rightPlaces));
  return scaledA < scaledB ? -1 : scaledA > scaledB ? 1 : 0;
}

function formatMoney(cents: string | null, currency: string) {
  if (cents == null || !/^[+-]?\d+$/.test(cents)) return "Unavailable";
  try {
    const formatter = new Intl.NumberFormat(undefined, { style: "currency", currency });
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    const amount = BigInt(cents), magnitude = amount < 0n ? -amount : amount, scale = 10n ** BigInt(digits);
    const whole = magnitude / scale;
    const fraction = (magnitude % scale).toString().padStart(digits, "0");
    const parts = formatter.formatToParts(amount < 0n ? whole === 0n ? -1n : -whole : whole);
    return parts.map(part => part.type === "fraction" ? fraction
      : part.type === "integer" && amount < 0n && whole === 0n ? "0" : part.value).join("");
  } catch {
    return "Unavailable";
  }
}

function formatPercent(value: number | null) {
  return value == null || !Number.isFinite(value) ? "Unavailable" : `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}

function compareItems(a: SquarePurchasesItem, b: SquarePurchasesItem, key: SortKey) {
  if (key === "quantity") return compareExactDecimal(a.quantity, b.quantity);
  if (key === "grossCents" || key === "discountCents" || key === "taxCents" || key === "collectedCents")
    return compareExactDecimal(a[key], b[key]);
  if (key === "percentOfTotal") return (a.percentOfTotal ?? Number.NEGATIVE_INFINITY) - (b.percentOfTotal ?? Number.NEGATIVE_INFINITY);
  return a[key].localeCompare(b[key], undefined, { sensitivity: "base" });
}

function SortHeader({ label, sortKey, selected, direction, onSort, align = "left" }: {
  label: string; sortKey: SortKey; selected: SortKey; direction: "asc" | "desc";
  onSort: (key: SortKey) => void; align?: "left" | "right";
}) {
  const active = selected === sortKey;
  return (
    <th scope="col" aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}
      className={`whitespace-nowrap px-3 py-2.5 text-xs font-semibold text-muted-foreground ${align === "right" ? "text-right" : "text-left"}`}>
      <button type="button" onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${align === "right" ? "justify-end" : ""}`}>
        {label}
        {active ? direction === "asc" ? <ArrowUp className="h-3 w-3" aria-hidden="true" /> : <ArrowDown className="h-3 w-3" aria-hidden="true" /> : null}
      </button>
    </th>
  );
}

export function SquarePurchasesSection({ brandId, accountId, since, until, enabled = true, refreshActive = false, progress, onFetchingChange }: {
  brandId: string;
  accountId: string;
  since: string;
  until: string;
  enabled?: boolean;
  refreshActive?: boolean;
  progress?: SquarePurchasesProgress;
  onFetchingChange?: (isFetching: boolean) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>({ key: "collectedCents", direction: "desc" });
  const [pagination, setPagination] = useState({ scope: "", index: 0 });
  const [refreshStartedAt, setRefreshStartedAt] = useState<number | null>(null);
  useEffect(() => {
    setRefreshStartedAt(refreshActive ? Date.now() : null);
  }, [refreshActive, brandId, since, until]);
  const rangeValid = /^\d{4}-\d{2}-\d{2}$/.test(since) && /^\d{4}-\d{2}-\d{2}$/.test(until) && since <= until;
  const params = new URLSearchParams({ brandId, from: since, to: until });
  const url = `/api/square/purchases?${params.toString()}`;
  const query = useQuery<SquarePurchases>({
    queryKey: ["/api/square/purchases", brandId, accountId, since, until],
    enabled: enabled && !!brandId && rangeValid,
    queryFn: async ({ signal }) => {
      const response = await fetch(url, { credentials: "include", signal });
      if (!response.ok) throw new Error("Purchase data could not be loaded. Please try again.");
      return response.json() as Promise<SquarePurchases>;
    },
    staleTime: 30_000,
    refetchInterval: () => refreshActive && refreshStartedAt != null
      && Date.now() - refreshStartedAt < 90_000 ? 3_000 : false,
  });
  const response = query.data;
  const report = enabled && response?.range?.from === since && response.range.to === until ? response : undefined;
  const rangeAttempted = !!report && report.coverage.requestedLocationDays > 0
    && report.coverage.attemptedLocationDays >= report.coverage.requestedLocationDays;
  const rangeQueued = !!progress?.remainingOrderDays
    && (progress.nextOrderDay ? progress.nextOrderDay >= since : !rangeAttempted);
  const collectingRange = enabled && refreshActive && progress?.state !== "paused"
    && (progress ? rangeQueued : !!report && !rangeAttempted);
  const detailBusy = enabled && (query.isLoading || collectingRange);
  const activityKey = JSON.stringify([brandId, accountId, since, until]);
  const previousActivity = useRef({ key: activityKey, active: refreshActive });
  useEffect(() => {
    const previous = previousActivity.current;
    previousActivity.current = { key: activityKey, active: refreshActive };
    // One final retained read captures completion after bounded active polling.
    if (enabled && rangeValid && previous.key === activityKey && previous.active && !refreshActive)
      void query.refetch();
  }, [activityKey, refreshActive, enabled, rangeValid, query.refetch]);
  useEffect(() => {
    onFetchingChange?.(detailBusy);
  }, [detailBusy, onFetchingChange]);
  const sortedItems = useMemo(() => {
    if (!report) return [];
    return [...report.items].sort((a, b) => {
      const order = compareItems(a, b, sort.key);
      return sort.direction === "asc" ? order : -order;
    });
  }, [report?.items, sort]);
  const paginationScope = JSON.stringify([activityKey, sort.key, sort.direction]);
  const pageCount = Math.max(1, Math.ceil(sortedItems.length / ITEMS_PER_PAGE));
  const pageIndex = pagination.scope === paginationScope ? Math.min(pagination.index, pageCount - 1) : 0;
  const pageStart = pageIndex * ITEMS_PER_PAGE;
  const visibleItems = sortedItems.slice(pageStart, pageStart + ITEMS_PER_PAGE);
  useEffect(() => {
    setPagination(current => ({
      scope: paginationScope,
      index: current.scope === paginationScope ? Math.min(current.index, pageCount - 1) : 0,
    }));
  }, [paginationScope, pageCount]);
  const changeSort = (key: SortKey) => setSort(current => ({
    key,
    direction: current.key === key ? current.direction === "asc" ? "desc" : "asc" : "asc",
  }));

  const showTable = !!report && (
    report.status === "available" || report.status === "partial"
    || report.status === "failed" && (report.items.length > 0 || report.residualCents != null)
  );
  const currency = report?.currency || "USD";

  return (
    <section data-testid="square-purchases-section" className="relative border-t border-slate-200 pt-4">
        <div className="mb-3 pr-8">
          <h3 className="text-sm font-semibold text-foreground">Collected sales by line item</h3>
        </div>
        <details className="open:mb-3" data-testid="square-purchases-details">
          <summary aria-label="About Square line-item data" title="About this data"
            className="absolute right-0 top-4 cursor-pointer list-none rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
            <Info className="h-4 w-4" aria-hidden="true" />
          </summary>
          <ul className="list-disc space-y-1 rounded-lg bg-slate-50 py-3 pl-7 pr-3 text-xs leading-relaxed text-muted-foreground">
            <li>Item sales use order dates, so they may differ from the sales total above.</li>
            <li>Refunds aren’t deducted; their impact is unknown.</li>
            <li>“Other” is an unexplained difference between order totals and item totals.</li>
            {report && (!report.coverage.orderTotalsComplete || !report.coverage.itemDetailsComplete) &&
              <li>Some {report.coverage.orderTotalsComplete ? "item details" : report.coverage.itemDetailsComplete ? "orders" : "orders or item details"} may be missing.</li>}
            {report && report.locations.length > 1 && <li>Includes all {report.locations.length} configured locations.</li>}
          </ul>
        </details>
        {query.isError && !report && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4" role="alert">
            <p className="text-sm font-medium">Purchase data could not be loaded.</p>
            <p className="mt-1 text-sm text-muted-foreground">{query.error instanceof Error ? query.error.message : "Please try again."}</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => void query.refetch()}>
              <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />Retry
            </Button>
          </div>
        )}
        {query.isError && report && (
          <p className="mb-3 text-xs text-muted-foreground" role="status">
            The latest detail read failed. Showing retained data for this same date range.
          </p>
        )}
        {query.isLoading && enabled && (
          <div className="min-h-20" aria-busy="true">
            <span className="sr-only" role="status">Loading order-date line-item detail</span>
          </div>
        )}
        {report && (
          <>
            {(collectingRange || report.status === "failed" || report.status === "pending" || !rangeAttempted && progress?.state === "paused") &&
              <p className="mb-3 text-xs text-muted-foreground" role={report.status === "failed" ? "alert" : "status"}
                data-testid="square-purchases-range-status">
                {report.status === "failed" ? "Line-item refresh failed. Retained data is shown where available."
                  : collectingRange ? "Refreshing line items for these dates…"
                  : progress?.state === "paused" ? "Refresh paused. Use Refresh to continue."
                  : "Waiting for line-item data for these dates."}
              </p>}

            {report.status === "empty" && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-6 text-center" data-testid="square-purchases-empty">
                <p className="text-sm font-medium">No completed orders to show</p>
                <p className="mt-1 text-xs text-muted-foreground">No order detail was returned for the Performance Brief date range.</p>
              </div>
            )}
            {report.status === "unavailable" && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 px-4 py-6 text-center" data-testid="square-purchases-unavailable">
                <p className="text-sm font-medium">Purchase detail is unavailable</p>
                <p className="mt-1 text-xs text-muted-foreground">This report does not infer zero sales from unavailable data.</p>
              </div>
            )}
            {showTable && (
              <>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="square-purchases-table">
                    <caption className="sr-only">Square purchase item detail for all configured locations</caption>
                    <thead className="bg-slate-50/80">
                      <tr>
                        <SortHeader label="Item" sortKey="name" selected={sort.key} direction={sort.direction} onSort={changeSort} />
                        <SortHeader label="Variation" sortKey="variationName" selected={sort.key} direction={sort.direction} onSort={changeSort} />
                        <SortHeader label="Qty" sortKey="quantity" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                        <SortHeader label="Gross" sortKey="grossCents" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                        <SortHeader label="Discounts" sortKey="discountCents" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                        <SortHeader label="Tax" sortKey="taxCents" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                        <SortHeader label="Collected" sortKey="collectedCents" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                        <SortHeader label="% of total" sortKey="percentOfTotal" selected={sort.key} direction={sort.direction} onSort={changeSort} align="right" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {visibleItems.map((item, index) => (
                        <tr key={`${item.name}-${item.variationName}-${index}`} className="hover:bg-slate-50/70">
                          <th scope="row" className="max-w-[220px] px-3 py-2.5 text-left font-medium text-foreground">{item.name}</th>
                          <td className="max-w-[180px] px-3 py-2.5 text-muted-foreground">{item.variationName || "Not specified"}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{item.quantity}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{formatMoney(item.grossCents, currency)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{formatMoney(item.discountCents, currency)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{formatMoney(item.taxCents, currency)}</td>
                          <td className="px-3 py-2.5 text-right font-medium tabular-nums">{formatMoney(item.collectedCents, currency)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{formatPercent(item.percentOfTotal)}</td>
                        </tr>
                      ))}
                      {pageIndex === pageCount - 1 && <tr data-testid="square-purchases-residual" className="border-t-2 border-slate-200 bg-slate-50/80 font-medium">
                        <th scope="row" colSpan={2} className="px-3 py-2.5 text-left">
                          Other
                          <span className="ml-1.5 font-normal text-muted-foreground">(tips, service charges, unallocated)</span>
                        </th>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">Not itemized</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">Not itemized</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">Not itemized</td>
                        <td className="px-3 py-2.5 text-right text-muted-foreground">Not itemized</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{formatMoney(report.residualCents, currency)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{formatPercent(report.residualPercent)}</td>
                      </tr>}
                    </tbody>
                  </table>
                </div>
                {sortedItems.length > 0 && <nav aria-label="Line item pagination" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                  <p aria-live="polite">Items {pageStart + 1}-{Math.min(pageStart + ITEMS_PER_PAGE, sortedItems.length)} of {sortedItems.length}</p>
                  <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" size="sm" disabled={pageIndex === 0}
                      aria-label="Previous line item page"
                      onClick={() => setPagination({ scope: paginationScope, index: pageIndex - 1 })}>Previous</Button>
                    <span>Page {pageIndex + 1} of {pageCount}</span>
                    <Button type="button" variant="outline" size="sm" disabled={pageIndex >= pageCount - 1}
                      aria-label="Next line item page"
                      onClick={() => setPagination({ scope: paginationScope, index: pageIndex + 1 })}>Next</Button>
                  </div>
                </nav>}
              </>
            )}
          </>
        )}
    </section>
  );
}
